import {
  CustomFieldData,
  CustomFieldDataResponse,
  NumericCustomFiled,
} from "@portal/types/entities/custom-field";
import { Unpacked } from "@portal/types/utils";
import {
  getStoredState,
  GridColumnProps,
  GridProps,
} from "@sinecycle/growcomponents";
import { find } from "lodash";
import { useMemo, useState } from "react";

function useCustomFieldColumns<T>(
  columnsDefs: GridProps<T>["columnDefs"],
  storageKey: string
) {
  const [cfColumns, setCfColumns] = useState<GridProps<T>["columnDefs"]>();
  const [columnLoading, setColumnLoading] = useState<boolean>(true);
  const storedColumns = getStoredState("LOCAL", storageKey);

  const colIds =
    storedColumns
      ?.filter((column) => column.visible)
      ?.map((column) => column.column) ?? [];

  function handleSuccessCallback(data: CustomFieldDataResponse[]) {
    if (!data?.length) {
      setColumnLoading(false);
      return;
    }

    const cfColumns: GridColumnProps<T> = data.map((field) => {
      const initialWidth = find(storedColumns, {
        column: field.display_name,
      })?.width;
      return {
        headerName: `${field.display_name}`,
        colId: field.display_name,
        cellRenderer: "customColumn",
        sortable: false,
        headerComponent: "cfHeader",
        hide: colIds.includes(field.display_name) ? true : undefined,
        headerClass: ["NUMBER", "DECIMAL"].includes(
          field.data_type as Unpacked<NumericCustomFiled>
        )
          ? "ag-right-aligned-header"
          : undefined,
        cellClass: ["NUMBER", "DECIMAL"].includes(
          field.data_type as Unpacked<NumericCustomFiled>
        )
          ? "ag-right-aligned-cell"
          : undefined,
        valueGetter: (params) => {
          const currentValue = find((params?.data as any)?.custom_field_data, {
            custom_field_data: field.id,
          }) as CustomFieldData;

          return currentValue
            ? {
                value: currentValue?.value ?? "-",
                type: field.data_type,
              }
            : "-";
        },
        initialWidth,
        cellRendererParams: {
          columnCustom: {
            hasSymbol: true,
          },
        },
      };
    });
    setCfColumns(cfColumns);
    setColumnLoading(false);
  }

  function handleErrorCallBack() {
    setColumnLoading(false);
  }

  function getColumns() {
    return [...(columnsDefs ?? []), ...(cfColumns ?? [])];
  }
  const columns = useMemo(getColumns, [cfColumns, columnsDefs]);

  return { handleSuccessCallback, columns, columnLoading, handleErrorCallBack };
}

export { useCustomFieldColumns };
